import { Clock, Effect, Equal, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { defineMessageUnion } from "foldkit/message";
import { defineView } from "foldkit/submodel";
import type * as Update from "foldkit/update";
import { apiData, settle, start } from "../api/asyncData";
import { getRecords } from "../api/commands/records";
import {
	Relationship,
	RelationshipType,
	TYPES,
	createRelationshipPerson,
	getRelationships,
	writeRelationship,
} from "../api/commands/relationships";
import { toMessage } from "../api/errors";
import { Page } from "../api/schemas/common";
import { RecordSummary } from "../api/schemas/records";
import { Route, linkHref } from "../app/routes";
import { i18next } from "../i18n";
import { type Api, type Shared, appendPage } from "../pages/shared";
import { busyAttributes, busySpinner } from "./busy";

const Person = S.Struct({ id: S.String, name: S.String });
const List = S.Struct({
	items: S.Array(Relationship),
	nextCursor: S.Option(S.Number),
});
export const Model = S.Struct({
	ownerId: S.String,
	list: apiData(List).schema,
	open: S.Boolean,
	query: S.String,
	people: apiData(Page(RecordSummary)).schema,
	selected: S.Option(Person),
	type: RelationshipType,
	saving: S.Boolean,
	error: S.Option(S.String),
	notice: S.String,
	epoch: S.Number,
	listRevision: S.Number,
});
export type Model = typeof Model.Type;
export const init = (ownerId: string): Model => ({
	ownerId,
	list: AsyncData.Idle(),
	open: false,
	query: "",
	people: AsyncData.Idle(),
	selected: Option.none(),
	type: "related",
	saving: false,
	error: Option.none(),
	notice: "",
	epoch: 0,
	listRevision: 0,
});
const context = {
	nodeId: S.String,
	generation: S.Number,
	asOf: S.Option(S.String),
	epoch: S.Number,
};
export const Message = defineMessageUnion({
	Loaded: {
		...context,
		listRevision: S.Number,
		cursor: S.Number,
		result: getRelationships.Result,
	},
	Opened: {},
	Closed: {},
	Reloaded: {},
	LoadMore: {},
	ChangedQuery: { value: S.String },
	Searched: { cursor: S.Number },
	SearchCompleted: {
		...context,
		query: S.String,
		cursor: S.Number,
		result: getRecords.Result,
	},
	Selected: { id: S.String },
	ChangedType: { value: S.String },
	Saved: {},
	Removed: { personId: S.String, type: RelationshipType },
	WriteCompleted: {
		...context,
		remove: S.Boolean,
		result: writeRelationship.Result,
	},
	CreatePerson: {},
	PersonCreated: {
		...context,
		name: S.String,
		result: createRelationshipPerson.Result,
	},
});
export type Message = typeof Message.Type;
type Return = Update.Return<Model, Message, Api>;
export const Load = Command.define("LoadPersonRelationships", {
	args: { ...context, listRevision: S.Number, cursor: S.Number },
	messages: [Message.Loaded],
	execute: (args) =>
		getRelationships
			.run({ ...args, asOf: Option.getOrNull(args.asOf) })
			.pipe(Effect.map((result) => Message.Loaded({ ...args, result }))),
});
export const Search = Command.define("SearchRelationshipPeople", {
	args: { ...context, query: S.String, cursor: S.Number },
	messages: [Message.SearchCompleted],
	execute: (args) =>
		getRecords
			.run({ search: args.query, cursor: args.cursor, asOf: null })
			.pipe(
				Effect.map((result) => Message.SearchCompleted({ ...args, result })),
			),
});
export const Write = Command.define("SavePersonRelationship", {
	args: {
		...context,
		personId: S.String,
		type: RelationshipType,
		remove: S.Boolean,
	},
	messages: [Message.WriteCompleted],
	execute: (args) =>
		writeRelationship
			.run(args)
			.pipe(
				Effect.map((result) => Message.WriteCompleted({ ...args, result })),
			),
});
export const Create = Command.define("CreateRelatedPerson", {
	args: { ...context, name: S.String },
	messages: [Message.PersonCreated],
	execute: (args) =>
		Clock.currentTimeMillis.pipe(
			Effect.flatMap((now) =>
				createRelationshipPerson.run({
					name: args.name,
					timestamp: new Date(now).toISOString(),
				}),
			),
			Effect.map((result) => Message.PersonCreated({ ...args, result })),
		),
});
const t = (key: string, options?: Record<string, string>) =>
	i18next.t(`relationships.${key}`, options ?? {});
const ctx = (model: Model, shared: Shared) => ({
	nodeId: model.ownerId,
	generation: shared.generation,
	asOf: shared.asOf,
	epoch: model.epoch,
});
export const reload = (model: Model, shared: Shared): Return => ({
	model: {
		...model,
		list: start(model.list),
		listRevision: model.listRevision + 1,
	},
	commands: [
		Load({
			...ctx(model, shared),
			listRevision: model.listRevision + 1,
			cursor: 0,
		}),
	],
});
export const reset = (model: Model, shared: Shared): Return =>
	reload({ ...init(model.ownerId), epoch: model.epoch + 1 }, shared);
const write = (
	model: Model,
	shared: Shared,
	personId: string,
	type: Model["type"],
	remove: boolean,
): Return => ({
	model: { ...model, saving: true, error: Option.none(), notice: "" },
	commands: [Write({ ...ctx(model, shared), personId, type, remove })],
});
export const update = (
	model: Model,
	message: Message,
	shared: Shared,
): Return => {
	if (
		"nodeId" in message &&
		(message.nodeId !== model.ownerId ||
			message.generation !== shared.generation ||
			message.epoch !== model.epoch ||
			!Equal.equals(message.asOf, shared.asOf))
	)
		return { model };
	if (
		Option.isSome(shared.asOf) &&
		!["Loaded", "Reloaded", "LoadMore"].includes(message._tag)
	)
		return { model };
	if (
		model.saving &&
		!["WriteCompleted", "PersonCreated", "Loaded", "SearchCompleted"].includes(
			message._tag,
		)
	)
		return { model };
	return Message.match<Return>(message, {
		Loaded: ({ result, cursor, listRevision }) => {
			if (listRevision !== model.listRevision) return { model };
			const previous = AsyncData.getOrElse(model.list, () => ({
				items: [],
				nextCursor: Option.none<number>(),
			}));
			return {
				model: {
					...model,
					list: settle(
						model.list,
						Result.map(result, (page) => appendPage(previous, cursor, page)),
					),
				},
			};
		},
		Reloaded: () => reload(model, shared),
		LoadMore: () =>
			Option.match(
				AsyncData.getOrElse(model.list, () => ({
					nextCursor: Option.none<number>(),
				})).nextCursor,
				{
					onNone: () => ({ model }),
					onSome: (cursor) => ({
						model: { ...model, list: start(model.list) },
						commands: [
							Load({
								...ctx(model, shared),
								listRevision: model.listRevision,
								cursor,
							}),
						],
					}),
				},
			),
		Opened: () => ({
			model: { ...model, open: true, error: Option.none(), notice: "" },
		}),
		Closed: () => ({
			model: {
				...model,
				open: false,
				selected: Option.none(),
				people: AsyncData.Idle(),
				query: "",
				error: Option.none(),
			},
		}),
		ChangedQuery: ({ value }) => ({
			model: {
				...model,
				query: value,
				selected: Option.none(),
				people: AsyncData.Idle(),
			},
		}),
		Searched: ({ cursor }) => ({
			model: { ...model, people: start(model.people) },
			commands: [
				Search({ ...ctx(model, shared), query: model.query.trim(), cursor }),
			],
		}),
		SearchCompleted: ({ query, cursor, result }) => {
			if (query !== model.query.trim()) return { model };
			const previous = AsyncData.getOrElse(model.people, () => ({ data: [] }));
			return {
				model: {
					...model,
					people: settle(
						model.people,
						Result.map(result, (page) => ({
							...page,
							data: cursor === 0 ? page.data : [...previous.data, ...page.data],
						})),
					),
				},
			};
		},
		Selected: ({ id }) => {
			const person = AsyncData.getOrElse(model.people, () => ({
				data: [],
			})).data.find(
				(p) => p.id === id && p.type === "person" && p.id !== model.ownerId,
			);
			return { model: { ...model, selected: Option.fromNullishOr(person) } };
		},
		ChangedType: ({ value }) => ({
			model: {
				...model,
				type: TYPES.find((type) => type === value) ?? model.type,
			},
		}),
		Saved: () =>
			Option.match(model.selected, {
				onNone: () => ({ model }),
				onSome: (person) => write(model, shared, person.id, model.type, false),
			}),
		Removed: ({ personId, type }) => write(model, shared, personId, type, true),
		WriteCompleted: ({ result, remove }) =>
			Result.match(result, {
				onFailure: (error) => ({
					model: {
						...model,
						saving: false,
						error: Option.some(toMessage(error)),
					},
				}),
				onSuccess: () =>
					reload(
						{
							...model,
							saving: false,
							open: remove ? model.open : false,
							selected: remove ? model.selected : Option.none(),
							notice: t(remove ? "removed" : "saved"),
							error: Option.none(),
						},
						shared,
					),
			}),
		CreatePerson: () =>
			model.query.trim() === ""
				? { model }
				: {
						model: { ...model, saving: true, error: Option.none() },
						commands: [
							Create({ ...ctx(model, shared), name: model.query.trim() }),
						],
					},
		PersonCreated: ({ name, result }) =>
			Result.match(result, {
				onFailure: (error) => ({
					model: {
						...model,
						saving: false,
						error: Option.some(toMessage(error)),
					},
				}),
				onSuccess: ({ created }) =>
					write(
						{ ...model, selected: Option.some({ id: created, name }) },
						shared,
						created,
						model.type,
						false,
					),
			}),
	});
};

export const view = defineView<Model, Message, Shared & { ownerName: string }>(
	(model, shared, h) => {
		const readOnly = Option.isSome(shared.asOf);
		const busy =
			AsyncData.isLoading(model.list) || AsyncData.isRefreshing(model.list);
		const list = AsyncData.getOrElse(model.list, () => ({
			items: [],
			nextCursor: Option.none<number>(),
		}));
		const people = AsyncData.getOrElse(model.people, () => ({
			data: [],
			nextCursor: undefined,
		}));
		const searchBusy =
			AsyncData.isLoading(model.people) || AsyncData.isRefreshing(model.people);
		const button = (
			label: string,
			message: Message,
			disabled = false,
			busy = false,
		) =>
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-sm btn-outline"),
					h.AriaLabel(label),
					...busyAttributes(h, busy, disabled || model.saving),
					h.OnClick(message),
				],
				[busySpinner(h, busy), label],
			);
		const phrase = (type: string, name: string) =>
			t(`phrases.${type}`, { owner: shared.ownerName, person: name });
		return h.section(
			[
				h.AriaLabel(t("title")),
				h.Class(
					"card bg-base-200/40 border border-base-300 p-4 my-4 space-y-3",
				),
			],
			[
				h.div(
					[h.Class("flex items-center justify-between gap-2")],
					[
						h.h2([h.Class("font-semibold text-lg")], [t("title")]),
						readOnly ? h.empty : button(t("add"), Message.Opened()),
					],
				),
				h.p(
					[h.Role("status"), h.AriaLive("polite"), h.Class("text-sm")],
					[busy ? t("loading") : model.notice],
				),
				AsyncData.isFailure(model.list) || AsyncData.isStale(model.list)
					? h.div(
							[h.Role("alert")],
							[t("loadFailed"), button(t("retry"), Message.Reloaded())],
						)
					: h.empty,
				list.items.length === 0 && !busy && !AsyncData.isFailure(model.list)
					? h.p([h.Class("text-sm text-base-content/60")], [t("empty")])
					: h.empty,
				h.ul(
					[h.Class("space-y-2")],
					list.items.map((item) =>
						h.li(
							[h.Class("flex items-center justify-between gap-3")],
							[
								h.a(
									[
										h.Href(
											linkHref(
												shared.route,
												Route.Record({ id: item.personId, asOf: shared.asOf }),
											),
										),
										h.Class("link link-hover"),
									],
									[phrase(item.type, item.name)],
								),
								readOnly
									? h.empty
									: button(
											t("remove", {
												person: item.name,
												relationship: t(`types.${item.type}`),
											}),
											Message.Removed({
												personId: item.personId,
												type: item.type,
											}),
										),
							],
						),
					),
				),
				Option.isSome(list.nextCursor)
					? button(t("more"), Message.LoadMore(), false, busy)
					: h.empty,
				Option.isSome(model.error)
					? h.p([h.Role("alert"), h.Class("text-error")], [model.error.value])
					: h.empty,
				model.open && !readOnly
					? h.div(
							[h.Class("border-t border-base-300 pt-3 space-y-3")],
							[
								h.label(
									[h.Class("form-control block")],
									[
										h.span([h.Class("label")], [t("person")]),
										h.input([
											h.Type("search"),
											h.Class("input input-bordered w-full"),
											h.AriaLabel(t("person")),
											h.Value(model.query),
											h.Maxlength(256),
											h.Disabled(model.saving),
											h.OnInput((value) => Message.ChangedQuery({ value })),
										]),
									],
								),
								button(
									t("search"),
									Message.Searched({ cursor: 0 }),
									false,
									searchBusy,
								),
								searchBusy
									? h.p([h.Role("status")], [t("searching")])
									: h.empty,
								AsyncData.isFailure(model.people) ||
								AsyncData.isStale(model.people)
									? h.p([h.Role("alert")], [t("searchFailed")])
									: h.empty,
								h.ul(
									[h.Class("space-y-1")],
									people.data
										.filter(
											(p) => p.type === "person" && p.id !== model.ownerId,
										)
										.map((person) =>
											h.li(
												[],
												[
													button(
														`${person.name}${person.subtitle ? ` — ${person.subtitle}` : ""}`,
														Message.Selected({ id: person.id }),
													),
												],
											),
										),
								),
								people.nextCursor !== undefined
									? button(
											t("morePeople"),
											Message.Searched({ cursor: people.nextCursor }),
											false,
											searchBusy,
										)
									: h.empty,
								Option.isSome(model.selected)
									? h.p(
											[h.Role("status")],
											[t("selected", { person: model.selected.value.name })],
										)
									: h.empty,
								h.label(
									[h.Class("form-control block")],
									[
										h.span([h.Class("label")], [t("type")]),
										h.select(
											[
												h.Class("select select-bordered w-full"),
												h.AriaLabel(t("type")),
												h.Value(model.type),
												h.Disabled(model.saving),
												h.OnChange((value) => Message.ChangedType({ value })),
											],
											TYPES.map((type) =>
												h.option([h.Value(type)], [t(`types.${type}`)]),
											),
										),
									],
								),
								Option.isSome(model.selected)
									? h.p(
											[h.Class("text-sm")],
											[phrase(model.type, model.selected.value.name)],
										)
									: h.empty,
								h.div(
									[h.Class("flex gap-2 flex-wrap")],
									[
										button(
											t("save"),
											Message.Saved(),
											Option.isNone(model.selected),
											model.saving && Option.isSome(model.selected),
										),
										button(t("cancel"), Message.Closed()),
										model.query.trim() && Option.isNone(model.selected)
											? button(
													t("create", { name: model.query.trim() }),
													Message.CreatePerson(),
													false,
													model.saving,
												)
											: h.empty,
									],
								),
								h.p(
									[h.Class("text-xs text-base-content/60")],
									[t("visibility")],
								),
							],
						)
					: h.empty,
			],
		);
	},
);
